import {
  faArrowRight,
  faTableLayout,
  faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { Root } from "@radix-ui/react-accordion";
import { isEqual } from "lodash-es";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { ErrorBaseInfo } from "src/api/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import {
  EditorAccordionItem as AccordionItem,
  accordionRootClassName,
} from "src/base-components/EditorAccordionItem";
import { FormItem } from "src/base-components/FormItem";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { Select } from "src/base-components/Select";
import { Spinner } from "src/base-components/Spinner";
import { CaseTemplateDropdown } from "src/caseCreationNode/CaseTemplateDropdown";
import { EnumAssignmentCard } from "src/caseCreationNode/EnumAssignmentCard";
import { StatusAssignmentCard } from "src/caseCreationNode/StatusAssignmentCard";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useCaseQueue, useCaseQueues } from "src/cases/queries/queues";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { DeserializedCaseQueue } from "src/cases/queues/types";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import {
  CasePropertiesValue,
  CaseTemplateAdditionalInput,
  CaseTemplateFileCategoryDefinition,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import {
  CaseCreationNodeMeta,
  CaseCreationNodeMetaAssignedQueueTypeEnum,
  CaseCreationNodeMetaMappedValueSectionEnum,
  CaseCreationNodeMetaMappedValueTypeEnum,
  CaseCreationNodeMetaMappedValueValueTypeEnum,
} from "src/clients/flow-api";
import {
  CaseCreationNode,
  CaseCreationNodeData,
} from "src/constants/NodeDataTypes";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useSubmitForm } from "src/utils/useSubmitForm";

const FIELDS_WITH_THEIR_OWN_SECTION = ["priority", "status"];
const MAX_PREDEFINED_VALUES_TO_SHOW = 10;

type PropertyType =
  | CasePropertiesValue
  | CaseTemplateAdditionalInput
  | CaseTemplateFileCategoryDefinition;

const getValuesHelpText = (property: PropertyType): React.ReactNode => {
  if ("type" in property) {
    if (property.type === "enum" || property.type === "tags") {
      const values = property.values;

      if (values.length <= 0) return null;

      const displayedValues = values.slice(0, MAX_PREDEFINED_VALUES_TO_SHOW);
      const remainingCount = values.length - MAX_PREDEFINED_VALUES_TO_SHOW;

      return (
        <div className="space-y-1">
          <p className="font-inter-semibold-12px">Supported values:</p>
          <div className="flex flex-wrap gap-1">
            {displayedValues.map((v) => (
              <Pill key={v.value} size="sm" variant="dark-gray">
                <Pill.Text fontType="code">"{v.value}"</Pill.Text>
              </Pill>
            ))}
            {remainingCount > 0 && (
              <Pill size="sm" variant="dark-gray">
                <Pill.Text fontType="code">+ {remainingCount} more</Pill.Text>
              </Pill>
            )}
          </div>
        </div>
      );
    }

    if (property.type === "entity_ref") {
      return (
        <>
          This property expects an{" "}
          <Pill size="sm" variant="dark-gray">
            <Pill.Text fontType="code">entity ID</Pill.Text>
          </Pill>{" "}
          as input.
        </>
      );
    }

    if (property.type === "event_ref") {
      return (
        <>
          This property expects an{" "}
          <Pill size="sm" variant="dark-gray">
            <Pill.Text fontType="code">event ID</Pill.Text>
          </Pill>{" "}
          as input.
        </>
      );
    }
  }
  return null;
};

const getUpdatedDetailsMapping = (
  currentValue: CaseCreationNodeMeta["details_mapping"],
  selectedTemplateVersion: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
) => {
  const updatedDetailsMapping: CaseCreationNodeMeta["details_mapping"] = {};

  for (const [key, property] of Object.entries({
    ...selectedTemplateVersion.attributes.case_properties,
    ...selectedTemplateVersion.attributes.additional_inputs,
  })) {
    updatedDetailsMapping[key] = {
      type: CaseCreationNodeMetaMappedValueTypeEnum.NON_CONDITIONAL,
      value_type: FIELDS_WITH_THEIR_OWN_SECTION.includes(key)
        ? CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED
        : CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC,
      value: "",
      section:
        key in selectedTemplateVersion.attributes.case_properties
          ? CaseCreationNodeMetaMappedValueSectionEnum.PROPERTIES
          : CaseCreationNodeMetaMappedValueSectionEnum.ADDITIONAL,
    };
    if (
      isEqual(
        [
          updatedDetailsMapping[key].type,
          updatedDetailsMapping[key].value_type,
          updatedDetailsMapping[key].section,
        ],
        [
          currentValue[key]?.type,
          currentValue[key]?.value_type,
          currentValue[key]?.section,
        ],
      )
    ) {
      if (
        property.type !== "enum" ||
        property.values.map((v) => v.value).includes(currentValue[key].value)
      ) {
        updatedDetailsMapping[key].value = currentValue[key].value;
      }
    }
  }

  const fileCategories = selectedTemplateVersion.attributes.file_categories;
  if (fileCategories) {
    Object.keys(fileCategories).forEach((key) => {
      updatedDetailsMapping[key] = {
        type: CaseCreationNodeMetaMappedValueTypeEnum.NON_CONDITIONAL,
        value_type: CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC,
        value: currentValue[key]?.value || "",
        section: CaseCreationNodeMetaMappedValueSectionEnum.FILE_CATEGORIES,
      };
    });
  }

  // Handle sub_status if needed
  const statusProperty =
    selectedTemplateVersion.attributes.case_properties.status;
  // If the status property has some sub_values, we need to add a sub_status field
  if (
    statusProperty &&
    statusProperty.type === "enum" &&
    statusProperty.values.some(
      (value) => value.sub_values && value.sub_values.length > 0,
    )
  ) {
    updatedDetailsMapping.sub_status = {
      type: CaseCreationNodeMetaMappedValueTypeEnum.NON_CONDITIONAL,
      value_type: updatedDetailsMapping.status.value_type,
      value: "",
      section: CaseCreationNodeMetaMappedValueSectionEnum.PROPERTIES,
    };
  }

  return updatedDetailsMapping;
};

const isTemplateRelatedToQueue = (
  templateId: string,
  queue?: DeserializedCaseQueue,
) => {
  const templateIds =
    queue?.template_configs.map(({ template }) => template.id) ?? [];
  return templateIds?.some((id) => id === templateId);
};

type Props = {
  node: CaseCreationNode;
  displayedError: ErrorBaseInfo | undefined;
} & NodeEditorBaseProps<CaseCreationNodeData>;

enum QueueErrorTag {
  IsNotAnExistingQueue = "is_not_an_existing_queue",
  IsUnsupportedQueue = "is_unsupported_queue",
}

export const CaseCreationNodeEditor: React.FC<Props> = ({
  immutable,
  node,
  isReactive,
  onUpdate,
  displayedError,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const [accordionValue, setAccordionValue] = useState<string[]>([
    "case-details",
  ]);

  const templateDropdownButtonRef = useRef<HTMLDivElement | null>(null);

  const formMethods = useForm<CaseCreationNodeData>({
    mode: "onBlur",
    shouldFocusError: false,
    defaultValues: node.data,
    ...(isReactive && { values: node.data }),
  });
  useSubmitForm({
    onChange: (data: CaseCreationNodeData) => {
      onUpdate({ newData: data });
    },
    previousValues: node.data,
    disabled: isReactive,
    watch: formMethods.watch,
  });

  const getPropertyIcon = useGetCasePropertyIcon();

  useEffect(() => {
    if (displayedError) {
      if (displayedError.loc_type === "field") {
        formMethods.setError(displayedError.loc as keyof CaseCreationNodeData, {
          message: displayedError.msg as string,
        });
      } else if (
        displayedError.loc_type === "node" &&
        displayedError.field_errors?.length
      ) {
        displayedError.field_errors.forEach((fieldError) => {
          formMethods.setError(
            `details_mapping.${fieldError.field_name}` as keyof CaseCreationNodeData,
            {
              message: fieldError.message as string,
            },
          );
        });
      }
    }
  }, [displayedError, formMethods]);

  const currentTemplateValue = useWatch({
    control: formMethods.control,
    name: "template",
  });

  const currentDetailsMapping = useWatch({
    control: formMethods.control,
    name: "details_mapping",
  });

  const { data: templates } = useCaseTemplatesWithVersions();
  const { data: queues } = useCaseQueues();
  const { data: queue } = useCaseQueue(node.data.assigned_queue?.value);

  const selectedTemplateVersion = (() => {
    if (!currentTemplateValue?.id || !currentTemplateValue?.version_id) {
      return undefined;
    }

    const template = templates?.find((t) => t.id === currentTemplateValue.id);
    const version = template?.versions?.find(
      (v) => v.id === currentTemplateValue.version_id,
    );

    if (template && version) {
      return {
        template,
        version,
      };
    }

    return undefined;
  })();

  // If a template version changes after its been used to configure
  // this node, we need to update the details mapping
  useEffect(() => {
    if (
      currentTemplateValue &&
      selectedTemplateVersion &&
      currentTemplateValue.etag !==
        selectedTemplateVersion.version.attributes.etag
    ) {
      formMethods.setValue(
        "details_mapping",
        getUpdatedDetailsMapping(
          currentDetailsMapping,
          selectedTemplateVersion.version,
        ),
      );
      formMethods.setValue(
        "template.etag",
        selectedTemplateVersion.version.attributes.etag!,
      );
    }
  }, [
    formMethods,
    currentDetailsMapping,
    currentTemplateValue,
    selectedTemplateVersion,
  ]);

  const queueErrorTag = useMemo(() => {
    if (!node.data.assigned_queue?.value || !node.data.template?.id || !queue)
      return;

    const selectedQueue = queues?.find(
      (queue) => queue.id === node.data.assigned_queue?.value,
    );

    if (!selectedQueue) return QueueErrorTag.IsNotAnExistingQueue;

    if (!isTemplateRelatedToQueue(node.data.template?.id, queue)) {
      return QueueErrorTag.IsUnsupportedQueue;
    }
  }, [node.data.assigned_queue, node.data.template?.id, queues, queue]);

  const isFileCategoryProperty = (
    key: string,
    property: PropertyType,
  ): property is CaseTemplateFileCategoryDefinition => {
    return Boolean(
      selectedTemplateVersion?.version.attributes.file_categories?.[key],
    );
  };

  if (!templates || !queues) {
    return <Spinner />;
  }

  const sectionEmptyState = (
    <div className="rounded border">
      <EmptyState
        actionButton={{
          text: "Select template",
          onClick: () => {
            setAccordionValue(["case-details"]);
            templateDropdownButtonRef.current?.click();
          },
        }}
        description="Select the template you want to use to complete configuration"
        headline="No template selected"
        icon={faTableLayout}
      />
    </div>
  );

  const allProperties = {
    ...selectedTemplateVersion?.version.attributes.case_properties,
    ...selectedTemplateVersion?.version.attributes.additional_inputs,
    ...selectedTemplateVersion?.version.attributes.file_categories,
  };

  const allCustomPropertiesMappingKeys = Object.keys(currentDetailsMapping)
    .filter((key) => !FIELDS_WITH_THEIR_OWN_SECTION.includes(key))
    .sort((a, b) => a.localeCompare(b));

  const priorityProperty =
    selectedTemplateVersion?.version.attributes.case_properties.priority;
  const statusProperty =
    selectedTemplateVersion?.version.attributes.case_properties.status;

  return (
    <div>
      {queueErrorTag && (
        <Callout type="error">
          {queueErrorTag === QueueErrorTag.IsNotAnExistingQueue &&
            "Assigned queue was not found. Please reconfigure mapping to another queue."}
          {queueErrorTag === QueueErrorTag.IsUnsupportedQueue &&
            "Selected queue does not support the template"}
        </Callout>
      )}
      <Root
        className={twJoin(accordionRootClassName, "mt-4")}
        type="multiple"
        value={accordionValue}
        onValueChange={setAccordionValue}
      >
        <AccordionItem
          className="pb-4 pl-4"
          disabled={renderedInDiffView}
          title="Configure case details"
          value="case-details"
          headerAsChild
        >
          <Card className="w-full pb-4 pt-4">
            <FormItem>
              <FormItem.Label className="mb-2" isRequired>
                Case template
              </FormItem.Label>
              <Controller
                control={formMethods.control}
                name="template"
                render={({ field }) => (
                  <CaseTemplateDropdown
                    buttonRef={templateDropdownButtonRef}
                    dataLoc="create-case-template-dropdown"
                    disabled={immutable}
                    error={!!formMethods.formState.errors.template}
                    options={templates}
                    placeholder="Select a template and version"
                    selected={
                      selectedTemplateVersion
                        ? {
                            templateId: selectedTemplateVersion.template.id,
                            versionId: selectedTemplateVersion.version.id,
                          }
                        : undefined
                    }
                    onSelect={(selection) => {
                      const template = templates.find(
                        (t) => t.id === selection.templateId,
                      );
                      const version = template?.versions?.find(
                        (v) => v.id === selection.versionId,
                      );

                      if (template && version) {
                        field.onChange({
                          id: template.id,
                          version_id: version.id,
                          etag: version.attributes.etag,
                        });
                        formMethods.setValue(
                          "details_mapping",
                          getUpdatedDetailsMapping(
                            currentDetailsMapping,
                            version,
                          ),
                        );
                      }
                    }}
                  />
                )}
              />
            </FormItem>
          </Card>
          {!!allCustomPropertiesMappingKeys.length && (
            <Card className="mt-3 w-full space-y-2 pb-4 pt-4">
              {allCustomPropertiesMappingKeys.map((key) => {
                const property = allProperties?.[key];
                const isFileCategoryField = isFileCategoryProperty(
                  key,
                  property,
                );

                if (!property) return null;

                const description = isFileCategoryField
                  ? property.description
                  : null;

                const helpText = description || getValuesHelpText(property);

                const icon = getPropertyIcon(property);

                return (
                  <Controller
                    key={key}
                    control={formMethods.control}
                    name={`details_mapping.${key}`}
                    render={({ field }) => (
                      <div className="flex w-full items-center gap-2">
                        <div className="w-1/2">
                          <AutocompleteCodeInput
                            disabled={immutable}
                            error={
                              !!formMethods.formState.errors.details_mapping?.[
                                key
                              ]
                            }
                            placeholder={
                              isFileCategoryField
                                ? "data.files[]"
                                : "data.field"
                            }
                            value={field.value.value}
                            onChange={(value) => {
                              field.onChange({
                                ...field.value,
                                value,
                              });
                            }}
                          />
                        </div>
                        <div className="flex-shrink-0">
                          <Icon
                            color="text-gray-500"
                            icon={faArrowRight}
                            size="xs"
                          />
                        </div>
                        <div className="w-1/2">
                          <Input
                            placeholder={property.display_name ?? key}
                            prefixIcon={{ icon }}
                            suffixIcon={
                              helpText
                                ? {
                                    icon: faInfoCircle,
                                    helpText,
                                  }
                                : undefined
                            }
                            disabled
                            fullWidth
                          />
                        </div>
                      </div>
                    )}
                  />
                );
              })}
            </Card>
          )}
        </AccordionItem>
        <AccordionItem
          className="pb-4 pl-4"
          disabled={renderedInDiffView}
          title="Assign queue"
          value="assign-queue"
          headerAsChild
        >
          {selectedTemplateVersion ? (
            <Card className="w-full pb-4 pt-4">
              <FormItem>
                <FormItem.Label className="mb-2" isRequired>
                  Queue
                </FormItem.Label>
                <Controller
                  control={formMethods.control}
                  name="assigned_queue"
                  render={({ field }) => (
                    <Select
                      disabled={immutable}
                      errored={!!formMethods.formState.errors.assigned_queue}
                      options={queues?.map((queue) => ({
                        value: (
                          <Pill size="sm" variant="gray">
                            <Pill.Text>{queue.name}</Pill.Text>
                          </Pill>
                        ),
                        key: queue.id,
                      }))}
                      placeholder="Select the queue"
                      value={field.value?.value}
                      onChange={(value) => {
                        field.onChange({
                          type: CaseCreationNodeMetaAssignedQueueTypeEnum.NON_CONDITIONAL,
                          value_type: "fixed",
                          value,
                        });
                      }}
                    />
                  )}
                />
              </FormItem>
            </Card>
          ) : (
            sectionEmptyState
          )}
        </AccordionItem>
        {priorityProperty && (
          <AccordionItem
            className="pb-4 pl-4"
            disabled={renderedInDiffView}
            title="Assign priority"
            value="assign-priority"
            headerAsChild
          >
            {selectedTemplateVersion ? (
              <Controller
                control={formMethods.control}
                name="details_mapping.priority"
                render={({ field }) => (
                  <EnumAssignmentCard
                    error={
                      !!formMethods.formState.errors.details_mapping?.priority
                    }
                    fieldName="priority"
                    immutable={immutable}
                    property={priorityProperty}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            ) : (
              sectionEmptyState
            )}
          </AccordionItem>
        )}
        <AccordionItem
          className="pb-4 pl-4"
          disabled={renderedInDiffView}
          title="Assign status"
          value="assign-status"
          headerAsChild
        >
          {selectedTemplateVersion && statusProperty ? (
            <Controller
              control={formMethods.control}
              name="details_mapping.status"
              render={({ field }) => {
                const subStatusFormValue = formMethods.getValues(
                  "details_mapping.sub_status",
                );
                return (
                  <StatusAssignmentCard
                    enableSubStatus={!!subStatusFormValue}
                    error={
                      !!formMethods.formState.errors.details_mapping?.status ||
                      !!formMethods.formState.errors.details_mapping?.sub_status
                    }
                    fieldName="status"
                    immutable={immutable}
                    property={
                      statusProperty as Parameters<
                        typeof StatusAssignmentCard
                      >[0]["property"]
                    }
                    status={field.value.value}
                    subStatus={subStatusFormValue?.value}
                    valueType={field.value.value_type}
                    onChangeValue={(status, subStatus) => {
                      field.onChange({
                        ...field.value,
                        value: status,
                      });

                      if (subStatus) {
                        formMethods.setValue("details_mapping.sub_status", {
                          ...field.value,
                          value: subStatus,
                        });
                      } else {
                        formMethods.unregister("details_mapping.sub_status");
                      }
                    }}
                    onChangeValueType={(valueType) => {
                      field.onChange({
                        ...field.value,
                        value: "",
                        value_type: valueType,
                      });

                      const subStatus = formMethods.getValues(
                        "details_mapping.sub_status",
                      );
                      // Only reset the sub_status value_type if it was already set
                      if (subStatus) {
                        formMethods.setValue("details_mapping.sub_status", {
                          ...field.value,
                          value: "",
                          value_type: valueType,
                        });
                      }
                    }}
                  />
                );
              }}
            />
          ) : (
            sectionEmptyState
          )}
        </AccordionItem>
        <AccordionItem
          className="pb-4 pl-4"
          disabled={renderedInDiffView}
          title="Configure case input location"
          value="configure-case-input-location"
          headerAsChild
        >
          <p className="mb-4 text-gray-500 font-inter-normal-12px">
            Case input includes all the fields configured above — such as case
            details, queue, status, and priority. These values will be stored in
            the location you specify and made available to downstream nodes.
          </p>
          <Card className="w-full">
            <FormItem>
              <FormItem.Label className="mb-2">
                Select location for case input
              </FormItem.Label>
              <Controller
                control={formMethods.control}
                name="case_input_location"
                render={({ field }) => (
                  <AutocompleteCodeInput
                    disabled={immutable}
                    placeholder="field"
                    prefix="data."
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FormItem>
          </Card>
        </AccordionItem>
        {/* <AccordionItem
        className="space-y-3 pb-4 pl-4"
        disabled={renderedInDiffView}
        title="Advanced settings"
        value="advanced-settings"
        headerAsChild
      >
        <div className="flex w-full items-center justify-between">
          <div>
            <p className="text-gray-800 font-inter-semibold-13px">
              For test runs, create cases in
            </p>
            <p className="text-gray-500 font-inter-normal-12px">
              Test runs using the button in the UI
            </p>
          </div>
          <Controller
            control={formMethods.control}
            name="advanced_settings.test_run"
            render={({ field }) => (
              <Select
                options={[{ key: "sandbox", value: "Sandbox environment" }]}
                placeholder="Select environment"
                value={field.value}
                disabled
              />
            )}
          />
        </div>
        <div className="flex w-full items-center justify-between">
          <div>
            <p className="text-gray-800 font-inter-semibold-13px">
              For sandbox API, create cases in
            </p>
            <p className="text-gray-500 font-inter-normal-12px">
              API calls using the{" "}
              <Pill variant="gray" size="sm">
                <Pill.Text fontType="code">/sandbox/decide</Pill.Text>
              </Pill>{" "}
              endpoint
            </p>
          </div>
          <Controller
            control={formMethods.control}
            name="advanced_settings.sandbox"
            render={({ field }) => (
              <Select
                options={[{ key: "sandbox", value: "Sandbox environment" }]}
                placeholder="Select environment"
                value={field.value}
                disabled
              />
            )}
          />
        </div>
        <div className="flex w-full items-center justify-between">
          <div>
            <p className="text-gray-800 font-inter-semibold-13px">
              For live API, create cases in
            </p>
            <p className="text-gray-500 font-inter-normal-12px">
              API calls using the
              <Pill variant="gray" size="sm">
                <Pill.Text fontType="code">/decide</Pill.Text>
              </Pill>{" "}
              endpoint
            </p>
          </div>
          <Controller
            control={formMethods.control}
            name="advanced_settings.live"
            render={({ field }) => (
              <Select
                options={[{ key: "live", value: "Live environment" }]}
                placeholder="Select environment"
                value={field.value}
                disabled
              />
            )}
          />
        </div>
      </AccordionItem> */}
      </Root>
    </div>
  );
};
